import {
  Badge,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  SvgIcon,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import notificationStyles from "./Notification.styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector } from "utils/hooks";
import {
  selectAccessToken,
  selectAuthenticated,
  selectDeviceId,
  selectRefreshToken,
} from "redux/authSlice";
import { checkIdToken } from "utils/service";
import { usePopover } from "hooks/use-popover";
import NotificationIcon from "assets/icons1/TopNavIcons/NotificationIcon.svg";
import NoNotificationIcon from "assets/icons1/NoNotificationIcon.svg";
import CustomPopper from "global/components/CustomPopper/CustomPopper";
import MarkAsRead from "assets/icons1/MarkAsRead.svg";
import DeleteAll from "assets/icons1/DeleteAll.svg";
import {
  calculateRelativeTime,
  handleErrorMessage,
  isTruthy,
} from "helpers/methods";
import { Notification as NotificationData } from "./NotificationTypes";
import CloseIcon from "@mui/icons-material/Close";
import {
  deleteAllNotifications,
  deleteNotification,
  markAsRead,
  markAsReadAll,
} from "./NotificationService";
import { selectTheme } from "redux/preferencesSlice";

const Notification = () => {
  const classes = notificationStyles;
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const preferredTheme = useAppSelector(selectTheme);
  const isLoggedIn = useAppSelector(selectAuthenticated);
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [intervalId, setIntervalId] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [hasMoreNotifs, setHasMoreNotifs] = useState<boolean>(false);
  const accessToken = useAppSelector(selectAccessToken);
  const refreshToken = useAppSelector(selectRefreshToken);
  const deviceId = useAppSelector(selectDeviceId);
  const observer = useRef<IntersectionObserver | null>(null);
  const notifs = useCallback(
    (node) => {
      if (observer.current) {
        (observer.current as IntersectionObserver).disconnect();
      }
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          if (totalCount >= 5 * page) {
            setPage((page) => page + 1);
            setHasMoreNotifs(true);
          } else {
            setHasMoreNotifs(false);
          }
        }
      });
      if (node && observer.current) {
        const intersectionObserver = observer.current as IntersectionObserver;
        intersectionObserver.observe(node);
      }
    },
    [notifications.length]
  );

  const notificationPopover = usePopover<any>();

  const notificationWorker: Worker = useMemo(
    () =>
      new Worker(
        new URL("workers/Notification/notifications.worker", import.meta.url)
      ),
    []
  );

  useEffect(() => {
    return () => {
      notificationWorker.terminate();
    };
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      initNotificationPolling();
    } else {
      stopNotificationPolling();
    }
  }, [accessToken, refreshToken, page, isLoggedIn]);

  const initNotificationPolling = () => {
    notificationWorker.postMessage({
      type: "INIT",
      data: {
        page,
        accessToken,
        deviceId,
        intervalId,
      },
    });
  };

  const stopNotificationPolling = () => {
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    if (notificationWorker) {
      notificationWorker.terminate();
    }
  };

  const handleAllNotifs = async (type: "READ" | "DELETE") => {
    try {
      if (type === "READ") {
        await markAsReadAll();
      }
      if (type === "DELETE") {
        await deleteAllNotifications();
      }
      initNotificationPolling();
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const handleNotification = async (type: "READ" | "DELETE", id: string) => {
    try {
      if (type === "READ") {
        await markAsRead(id);
      }
      if (type === "DELETE") {
        await deleteNotification(id);
      }
      initNotificationPolling();
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  // Data received from the worker thread
  notificationWorker.onmessage = async (payload: any) => {
    const { type, data } = payload.data;
    switch (type) {
      case "INTERVAL_ID": {
        setIntervalId(data.intervalId);
        break;
      }
      case "NOTIFICATION": {
        setUnreadCount(data.unreadCount);
        setTotalCount(data.totalCount);
        setNotifications(data.notifications);
        break;
      }
      case "TOKEN_EXPIRED": {
        try {
          await checkIdToken(accessToken, refreshToken);
        } catch (error: any) {
          if (intervalId) {
            clearInterval(intervalId);
            setIntervalId(null);
          }
        }
        break;
      }
      case "INTERNAL_SERVER_ERROR": {
        stopNotificationPolling();
        break;
      }
      default: {
        break;
      }
    }
  };

  const getNotificationsView = () => {
    if (!isTruthy(notifications)) {
      return (
        <Stack alignItems="center" spacing={1} my={5}>
          <img
            src={NoNotificationIcon}
            alt="Notification Icon"
            style={{
              filter: "brightness(90%)",
              height: "150px",
              width: "150px",
            }}
          />
          <Stack alignItems="center">
            <Typography variant="subtitle1">No notification yet</Typography>
            <Typography variant="subtitle2">
              You have no notifications right now.
            </Typography>
          </Stack>
        </Stack>
      );
    }
    return notifications.map((notification, index: number) => {
      return (
        <Stack
          mb={1}
          spacing={0.5}
          p={1}
          sx={[
            !notification.read && {
              backgroundColor:
                preferredTheme === "light" ? "#F0FBE3" : "#15202B",
            },
            { borderRadius: "8px" },
          ]}
          id={notifications.length === index + 1 ? "ref" : undefined}
          ref={notifications.length === index + 1 ? notifs : undefined}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="subtitle2" flex={2}>
              {notification.title}
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              flex={1}
              justifyContent="flex-end"
            >
              <IconButton
                size="small"
                onClick={() =>
                  handleNotification("READ", notification.notificationId)
                }
              >
                <img
                  src={MarkAsRead}
                  alt="mark as read"
                  style={{ width: "20px", height: "20px" }}
                />
              </IconButton>
              <IconButton
                size="small"
                onClick={() =>
                  handleNotification("DELETE", notification.notificationId)
                }
              >
                <SvgIcon>
                  <CloseIcon fontSize="small" htmlColor="#F04438" />
                </SvgIcon>
              </IconButton>
            </Stack>
          </Stack>
          <Typography variant="body2" color="grey">
            {notification.description}
          </Typography>
          <Typography variant="body2" color="grey" sx={{ fontSize: "12px" }}>
            {calculateRelativeTime(notification.createdAt)}
          </Typography>
        </Stack>
      );
    });
  };

  const getNotificationSection = () => {
    return (
      <Stack spacing={0.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="body1">Notifications</Typography>
          <Stack
            direction="row"
            divider={<Divider orientation="vertical" flexItem />}
          >
            <Button color="success" onClick={() => handleAllNotifs("READ")}>
              {isDesktop ? (
                "Mark as Read"
              ) : (
                <img
                  src={MarkAsRead}
                  alt="mark as read"
                  style={{ width: "20px", height: "20px" }}
                />
              )}
            </Button>
            <Button color="error" onClick={() => handleAllNotifs("DELETE")}>
              {isDesktop ? (
                "Clear all"
              ) : (
                <img
                  src={DeleteAll}
                  alt="delete all"
                  style={{ width: "15px", height: "15px" }}
                />
              )}
            </Button>
          </Stack>
        </Stack>
        <Divider />
        <Stack sx={{ maxHeight: "350px", overflowY: "auto" }}>
          {getNotificationsView()}
          {hasMoreNotifs && notifications.length > 0 && (
            <Stack justifyContent="center" alignItems="center" my={2}>
              <CircularProgress size="1.5rem" />
            </Stack>
          )}
        </Stack>
      </Stack>
    );
  };

  return (
    <>
      <Grid
        mt={1}
        mr={3}
        sx={{
          ...classes.cursor,
          height: "fit-content",
        }}
        onClick={notificationPopover.handleOpen}
        ref={notificationPopover.anchorRef}
      >
        <Tooltip title="Notifications" placement="bottom" arrow>
          <Badge badgeContent={notifications.length && unreadCount}>
            <img
              src={NotificationIcon}
              alt="Notification icon"
              style={{ width: "1.85rem", height: "fit-content" }}
            />
          </Badge>
        </Tooltip>
      </Grid>

      <CustomPopper
        open={notificationPopover.open}
        anchorRef={notificationPopover.anchorRef.current}
        handleClose={notificationPopover.handleClose}
        children={getNotificationSection()}
        width={isDesktop ? "25rem" : "25rem"}
      />
    </>
  );
};

export default Notification;
